// =============================================================================
// Bộ lọc danh sách Hạ COD: URL ⇄ filter + áp lên PostgREST builder. Thuần, có test.
//
// 2 "cột" lọc là COMPUTED FIELD của PostgREST (hàm SQL nhận dòng đề nghị):
//   - cod_reduction_is_my_turn        → tab "Chờ tôi duyệt" (cùng 1 luật với RPC duyệt)
//   - cod_reduction_order_reconciled_at → đơn đã/chưa đối soát COD (kế toán tìm đơn cần
//     sửa phiếu thu tự động). Không embed orders: RLS đơn chặn người xem đơn hẹp.
// =============================================================================
import type { CodReductionListFilter, CodReductionReconciledFilter, CodReductionStatus } from '@/types/db'
import { COD_REDUCTION_STATUSES } from './cod-reduction-utils'
import { parseOrderIdQuery } from '@/features/orders/orders-search'

export const RECONCILED_FILTER_OPTIONS: { code: CodReductionReconciledFilter; label: string }[] = [
  { code: 'no', label: 'Đơn chưa đối soát COD' },
  { code: 'yes', label: 'Đơn đã đối soát COD' },
]

export function parseStatusFilter(v: string | null | undefined): CodReductionStatus | 'all' {
  return (COD_REDUCTION_STATUSES as readonly string[]).includes(v ?? '') ? (v as CodReductionStatus) : 'all'
}

export function parseReconciledFilter(v: string | null | undefined): CodReductionReconciledFilter | null {
  return v === 'yes' || v === 'no' ? v : null
}

function numOrNull(v: string | null): number | null {
  if (v == null || v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// Tham số URL (đồng bộ thanh địa chỉ) → bộ lọc. Không gồm page/pageSize/my_turn.
export function filterFromParams(sp: URLSearchParams): CodReductionListFilter {
  return {
    status: parseStatusFilter(sp.get('status')),
    search: sp.get('q')?.trim() || undefined,
    created_by: sp.get('creator') || null,
    sale_user_id: sp.get('sale') || null,
    reason_id: numOrNull(sp.get('reason')),
    provider: sp.get('prov') || null,
    from: sp.get('from') || null,
    to: sp.get('to') || null,
    completedFrom: sp.get('cfrom') || null,
    completedTo: sp.get('cto') || null,
    amount_min: numOrNull(sp.get('amin')),
    amount_max: numOrNull(sp.get('amax')),
    reconciled: parseReconciledFilter(sp.get('rec')),
  }
}

// 'YYYY-MM-DD' → mốc đầu ngày / đầu NGÀY KẾ (cận trên loại trừ) theo giờ máy — như Duyệt chi.
export function dayStartIso(d: string): string {
  return new Date(`${d}T00:00:00`).toISOString()
}
export function nextDayStartIso(d: string): string {
  const next = new Date(`${d}T00:00:00`)
  next.setDate(next.getDate() + 1)
  return next.toISOString()
}

// Ký tự cú pháp của `.or()` PostgREST (phẩy, ngoặc) + wildcard (*, %) → khoảng trắng.
export function escapeSearch(v: string): string {
  return v.replace(/[,()*%\\]/g, ' ').trim()
}

// Tìm text trên các cột SNAPSHOT của đề nghị (không join): mã đề nghị, mã đơn, mã vận đơn, KH.
// Như trang Đơn hàng: "DH0008940" / "8940" còn khớp đúng đơn có ID đó (mã hiển thị = DH + ID).
export function buildSearchClause(search: string | undefined): string | null {
  const safe = escapeSearch(search ?? '')
  if (!safe) return null
  const parts = ['code', 'order_code', 'tracking_code', 'customer_name'].map((c) => `${c}.ilike.%${safe}%`)
  const orderId = parseOrderIdQuery(search ?? '')
  if (orderId != null) parts.push(`order_id.eq.${orderId}`)
  return parts.join(',')
}

// Builder tối thiểu của PostgREST — đủ để test bằng builder giả.
export type CodReductionFilterable<Q> = {
  eq(column: string, value: unknown): Q
  gte(column: string, value: unknown): Q
  lte(column: string, value: unknown): Q
  lt(column: string, value: unknown): Q
  is(column: string, value: null): Q
  not(column: string, operator: string, value: unknown): Q
  or(filters: string): Q
}

export function applyCodReductionFilter<Q extends CodReductionFilterable<Q>>(q: Q, f: CodReductionListFilter): Q {
  let out = q
  if (f.status && f.status !== 'all') out = out.eq('status', f.status)
  if (f.created_by) out = out.eq('created_by', f.created_by)
  if (f.sale_user_id) out = out.eq('sale_user_id', f.sale_user_id)
  if (f.reason_id != null) out = out.eq('reason_id', f.reason_id)
  if (f.provider) out = out.eq('provider', f.provider)
  if (f.amount_min != null) out = out.gte('reduce_amount', f.amount_min)
  if (f.amount_max != null) out = out.lte('reduce_amount', f.amount_max)
  if (f.from) out = out.gte('created_at', dayStartIso(f.from))
  if (f.to) out = out.lt('created_at', nextDayStartIso(f.to))
  if (f.completedFrom) out = out.gte('completed_at', dayStartIso(f.completedFrom))
  if (f.completedTo) out = out.lt('completed_at', nextDayStartIso(f.completedTo))
  if (f.reconciled === 'yes') out = out.not('cod_reduction_order_reconciled_at', 'is', null)
  if (f.reconciled === 'no') out = out.is('cod_reduction_order_reconciled_at', null)
  if (f.my_turn) out = out.eq('cod_reduction_is_my_turn', true)
  const clause = buildSearchClause(f.search)
  if (clause) out = out.or(clause)
  return out
}

// Có bộ lọc nâng cao nào đang bật (hiện nút "Xoá lọc").
export function hasExtraFilter(f: CodReductionListFilter): boolean {
  return Boolean(
    f.created_by ||
      f.sale_user_id ||
      f.reason_id != null ||
      f.provider ||
      f.from ||
      f.to ||
      f.completedFrom ||
      f.completedTo ||
      f.amount_min != null ||
      f.amount_max != null ||
      f.reconciled,
  )
}
